import { useRef, useState, useEffect, useImperativeHandle, forwardRef } from 'react';

import { AgGridReactProps } from 'ag-grid-react';

import { Box, Modal, ModalFooter, ModalHeader, AgGridTable, BodySmall } from '@parspec/pixel';

import { defaultAgGridProps } from './constant';
import { Set } from 'typescript';


interface ManageColumnsPropsT {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setSnackbarData: React.Dispatch<React.SetStateAction<string>>;
    allColumnOptions: { field: string; label: string }[];
    onColumnSelection: (data: string[]) => void;
    selectedColumnsData: string[] | null;
    isSelectedColumnsMutationLoading: boolean;
    isSelectedColumnsQueryFetching: boolean;
    fixedColumns?: Set<string>;
    agGridProps?: AgGridReactProps,
}

export const ManageColumnsPopup = forwardRef(({
    isOpen,
    setIsOpen,
    allColumnOptions,
    setSnackbarData,
    onColumnSelection,
    selectedColumnsData,
    isSelectedColumnsMutationLoading,
    isSelectedColumnsQueryFetching,
    fixedColumns,
    agGridProps = {}
}: ManageColumnsPropsT, ref) => {
    const [searchText, setSearchText] = useState('');
    const [rowDataOptions, setRowDataOptions] = useState<{ field: string; label: string }[]>([]);

    const tableRef = useRef<any>();
    //forward-red the table/grid ref

    useEffect(() => {
        if (isSelectedColumnsQueryFetching) {
            return;
        }

        if (selectedColumnsData && selectedColumnsData.length > 0) {
            const allColumnOptionsObj = allColumnOptions.reduce((acc, curr) => {
                acc[curr.field] = curr;
                return acc;
            }, {} as { [key: string]: { field: string; label: string } });
            const selectedColumnDataSet = new Set(selectedColumnsData);
            const resultData = selectedColumnsData
                .map((field) => allColumnOptionsObj[field])
                .filter((item) => item !== undefined)
                .concat(allColumnOptions.filter((item) => !selectedColumnDataSet.has(item.field)));

            setRowDataOptions(resultData);
        } else {
            setRowDataOptions(allColumnOptions);
        }
    }, [allColumnOptions, isSelectedColumnsQueryFetching, selectedColumnsData]);

    function onGridReady() {
        if ((selectedColumnsData || [])?.length === 0) {
            tableRef.current.api.forEachNode((node: any) => {
                return node.setSelected(true);
            });
        } else {
            tableRef.current.api.forEachNode((node: any) => {
                if (selectedColumnsData?.includes(node.data.field)) {
                    node.setSelected(true);
                } else {
                    node.setSelected(false);
                }
            });
        }
    }

    const closeModalHandler = () => {
        setIsOpen(() => false);
    };

    const handleSearch = (searchString: string) => {
        setSearchText(searchString);
    };

    const onAccept = () => {
        const rowData: any[] = [];
        tableRef.current.api.forEachNode((node: any) => {
            if (node.selected) {
                rowData.push(node.data);
            }
        });

        if (rowData.length === 0) {
            return setSnackbarData('Please select at least one column');
        }

        const finalRowData = rowData.map((el: { field: string; label: string }) => el.field);
        onColumnSelection(finalRowData);
    };
    const handleSelectionChange = () => {
        tableRef.current.api.forEachNode((node: any) => {
            if (fixedColumns?.has(node.data.field)) {
                node.setSelected(true);
            }
        });

    };

    useImperativeHandle(
        ref,
        () => {
            return tableRef;
        },
        []
    );

    return (
        <Box>
            <Modal
                open={isOpen}
                footer={<ModalFooter isLoading={isSelectedColumnsMutationLoading} onAccept={onAccept} onReject={closeModalHandler} />}
                header={<ModalHeader onClose={closeModalHandler} title={'Manage Columns'}></ModalHeader>}
                onClose={closeModalHandler}
            >
                <Box width={'400px'}>
                    <Box mb={6}>
                        <BodySmall color={'text.secondary'} limit={false}>
                            Choose the columns to display, and the order in which they are to appear.{' '}
                        </BodySmall>
                    </Box>

                    <Box height={'400px'} width={'100%'}>
                        <AgGridTable
                            {...defaultAgGridProps}
                            ref={tableRef}
                            rowData={rowDataOptions}
                            onTextSearch={handleSearch}
                            quickFilterText={searchText}
                            onGridReady={onGridReady}
                            onSelectionChanged={handleSelectionChange}
                            {...agGridProps}
                        />
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
});